import axios from "axios";

// ─── Axios instance ───────────────────────────────────────────
export const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "",
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// ─── Token stored in memory only ─────────────────────────────
let accessToken = null;

export const getAccessToken = () => accessToken;
export const setAccessToken = (token) => {
  accessToken = token;
};
export const clearAccessToken = () => {
  accessToken = null;
};

// ─── Active project ───────────────────────────────────────────
// Every request carries the active project as an X-Project-Id header, so no
// service has to thread it through. ProjectContext owns this value and MUST
// set it before any page fetch runs (see context/ProjectContext.js).
//
// Mirrored into sessionStorage so a page refresh keeps you in the project you
// switched to. It is deliberately session-scoped: closing the tab (or logging
// out, which clears it) means the next login lands on your default project.
export const PROJECT_SESSION_KEY = "arc_active_project";

let activeProjectId = null;

export const getActiveProjectId = () => activeProjectId;

export const getStoredProjectId = () => {
  try {
    return sessionStorage.getItem(PROJECT_SESSION_KEY);
  } catch {
    return null;
  }
};

export const setActiveProjectId = (projectId) => {
  activeProjectId = projectId;
  try {
    if (projectId) {
      sessionStorage.setItem(PROJECT_SESSION_KEY, projectId);
    } else {
      sessionStorage.removeItem(PROJECT_SESSION_KEY);
    }
  } catch {
    // Private-browsing / storage disabled — the in-memory value still works.
  }
};

export const clearActiveProjectId = () => setActiveProjectId(null);

// ─── User loaded callback ─────────────────────────────────────
// Set by App.js on mount so axios can notify UserContext
// when a token refresh returns new user data.
let _onUserLoaded = null;

export const setUserLoadedCallback = (fn) => {
  _onUserLoaded = fn;
};

const notifyUserLoaded = (data) => {
  if (_onUserLoaded && data?.email) {
    _onUserLoaded({
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
    });
  }
};

// ─── Request interceptor — attach access token ────────────────
axiosInstance.interceptors.request.use(
  (config) => {
    if (accessToken) {
      config.headers["Authorization"] = `Bearer ${accessToken}`;
    }
    if (activeProjectId) {
      config.headers["X-Project-Id"] = activeProjectId;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ─── Response interceptor — silent refresh on 401 ─────────────
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url.includes("/api/users/")
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await axiosInstance.post("/api/users/refresh");
        const newToken = response.data.token;

        setAccessToken(newToken);
        notifyUserLoaded(response.data); // ← update UserContext on silent refresh
        processQueue(null, newToken);

        originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearAccessToken();
        clearActiveProjectId();
        window.dispatchEvent(new Event("auth:logout"));
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

// ─── Restore session on page load ────────────────────────────
export const restoreSession = async () => {
  try {
    const response = await axiosInstance.post("/api/users/refresh");
    setAccessToken(response.data.token);
    notifyUserLoaded(response.data); // ← update UserContext on restore
    return response.data;
  } catch {
    clearAccessToken();
    return null;
  }
};

// ─── Logout ───────────────────────────────────────────────────
export const logout = async () => {
  try {
    await axiosInstance.post("/api/users/logout");
  } finally {
    clearAccessToken();
    clearActiveProjectId();
  }
};

// ─── Standard request ─────────────────────────────────────────
export const request = (method, url, data) => {
  return axiosInstance({ method, url, data });
};

// ─── File upload ──────────────────────────────────────────────
export const uploadFile = (
  url,
  file,
  specName,
  documentType,
  createSuite,
  dbName,
  query,
) => {
  const formData = new FormData();
  formData.append("fileUpload", file);

  if (specName !== null && specName !== undefined) {
    formData.append(
      "requestBody",
      new Blob(
        [
          JSON.stringify({
            specName,
            documentType,
            createSuite,
            dbName,
            query,
          }),
        ],
        { type: "application/json" },
      ),
    );
  }

  return axiosInstance.post(url, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

// ─── Form data requests ───────────────────────────────────────
export const postFormData = (url, formData) => {
  return axiosInstance.post(url, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const postFormDataRun = (url, formData) => {
  return axiosInstance.post(url, formData);
};
